/**
 * API Tokens List Component
 *
 * Displays table of API tokens with actions (revoke, delete).
 * Includes filtering, pagination, and real-time status updates.
 */
import React, { useState, useEffect } from 'react';
import {
  Table,
  Badge,
  ActionIcon,
  Group,
  Text,
  Tooltip,
  Paper,
  Stack,
  Loader,
  Alert,
  Button,
  Menu,
  Modal
} from '@mantine/core';
import {
  IconTrash,
  IconBan,
  IconDots,
  IconAlertCircle,
  IconRefresh,
  IconCheck,
  IconClock,
  IconAlertTriangle
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';
import {
  fetchTokens,
  revokeToken,
  deleteToken
} from '../../../services/api/apiTokensService';

/**
 * APITokensList Component
 *
 * @param {Function} onRefresh - Callback when list should refresh
 */
const APITokensList = ({ onRefresh }) => {
  const [tokensList, setTokensList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Delete confirmation modal
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState(null);

  // Load tokens
  const loadTokens = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchTokens(0, 100, null);
      setTokensList(data.tokens || []);
    } catch (err) {
      console.error('Failed to load tokens:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTokens();
  }, [onRefresh]);

  // Handle revoke token
  const handleRevoke = async (tokenId, tokenName) => {
    const confirmed = window.confirm(
      `Деактивировать токен "${tokenName}"?\n\nТокен перестанет работать для аутентификации, но останется в базе данных.`
    );

    if (!confirmed) return;

    try {
      setActionLoading(tokenId);
      await revokeToken(tokenId);
      await loadTokens();
    } catch (err) {
      console.error('Failed to revoke token:', err);
      alert(`Ошибка: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle delete token (with modal)
  const handleDeleteClick = (token) => {
    setTokenToDelete(token);
    setDeleteModalOpened(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tokenToDelete) return;

    try {
      setActionLoading(tokenToDelete.id);
      await deleteToken(tokenToDelete.id);
      setDeleteModalOpened(false);
      setTokenToDelete(null);
      await loadTokens();
    } catch (err) {
      console.error('Failed to delete token:', err);
      alert(`Ошибка: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow'
    });
  };

  // Check if token is expiring soon (within 7 days)
  const isExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;
    const now = new Date();
    const expiration = new Date(expiresAt);
    const daysUntilExpiration = (expiration - now) / (1000 * 60 * 60 * 24);
    return daysUntilExpiration > 0 && daysUntilExpiration <= 7;
  };

  // Check if token is expired
  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  // Loading state
  if (loading) {
    return (
      <Paper p="xl" style={{ textAlign: 'center' }}>
        <Loader size="lg" color={tokens.colors.brand.primary} />
        <Text size="sm" c={tokens.colors.neutral[6]} mt="md">
          Загрузка токенов...
        </Text>
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Ошибка загрузки"
        color="red"
        variant="light"
      >
        <Stack gap="sm">
          <Text size="sm">{error}</Text>
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconRefresh size={14} />}
            onClick={loadTokens}
          >
            Повторить
          </Button>
        </Stack>
      </Alert>
    );
  }

  // Empty state
  if (tokensList.length === 0) {
    return (
      <Paper p="xl" style={{ textAlign: 'center' }}>
        <Text size="lg" c={tokens.colors.neutral[6]}>
          Токенов пока нет
        </Text>
        <Text size="sm" c={tokens.colors.neutral[5]} mt="xs">
          Создайте первый API токен для начала работы
        </Text>
      </Paper>
    );
  }

  return (
    <>
      <Paper shadow="sm" p={0} style={{ overflow: 'hidden' }}>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Text size="xs" fw={700} tt="uppercase" c={tokens.colors.neutral[7]}>
                  Название
                </Text>
              </Table.Th>
              <Table.Th>
                <Text size="xs" fw={700} tt="uppercase" c={tokens.colors.neutral[7]}>
                  Префикс
                </Text>
              </Table.Th>
              <Table.Th>
                <Text size="xs" fw={700} tt="uppercase" c={tokens.colors.neutral[7]}>
                  Права
                </Text>
              </Table.Th>
              <Table.Th>
                <Text size="xs" fw={700} tt="uppercase" c={tokens.colors.neutral[7]}>
                  Статус
                </Text>
              </Table.Th>
              <Table.Th>
                <Text size="xs" fw={700} tt="uppercase" c={tokens.colors.neutral[7]}>
                  Истекает
                </Text>
              </Table.Th>
              <Table.Th>
                <Text size="xs" fw={700} tt="uppercase" c={tokens.colors.neutral[7]}>
                  Последнее использование
                </Text>
              </Table.Th>
              <Table.Th style={{ width: '80px' }}>
                <Text size="xs" fw={700} tt="uppercase" c={tokens.colors.neutral[7]}>
                  Действия
                </Text>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {tokensList.map((token) => {
              const expired = isExpired(token.expires_at);
              const expiringSoon = isExpiringSoon(token.expires_at);

              return (
                <Table.Tr key={token.id}>
                  {/* Name */}
                  <Table.Td>
                    <Text size="sm" fw={500} c={tokens.colors.neutral[8]}>
                      {token.name}
                    </Text>
                  </Table.Td>

                  {/* Prefix */}
                  <Table.Td>
                    <Text
                      size="xs"
                      c={tokens.colors.neutral[6]}
                      style={{ fontFamily: 'Consolas, Monaco, monospace' }}
                    >
                      {token.token_prefix}
                    </Text>
                  </Table.Td>

                  {/* Scopes count */}
                  <Table.Td>
                    <Tooltip
                      label={
                        <Stack gap={4}>
                          {token.scopes.map((scope, idx) => (
                            <Text key={idx} size="xs" style={{ fontFamily: 'monospace' }}>
                              • {scope}
                            </Text>
                          ))}
                        </Stack>
                      }
                      withArrow
                      position="top"
                      multiline
                      w={300}
                    >
                      <Badge color="blue" variant="light" size="sm">
                        {token.scopes.length} {token.scopes.length === 1 ? 'право' : 'прав'}
                      </Badge>
                    </Tooltip>
                  </Table.Td>

                  {/* Status */}
                  <Table.Td>
                    {!token.is_active ? (
                      <Badge color="gray" variant="light" leftSection={<IconBan size={12} />}>
                        Отключен
                      </Badge>
                    ) : expired ? (
                      <Badge color="red" variant="light" leftSection={<IconAlertTriangle size={12} />}>
                        Истёк
                      </Badge>
                    ) : (
                      <Badge color="portal-green" variant="light" leftSection={<IconCheck size={12} />}>
                        Активен
                      </Badge>
                    )}
                  </Table.Td>

                  {/* Expires at */}
                  <Table.Td>
                    {token.expires_at ? (
                      expiringSoon && token.is_active ? (
                        <Tooltip label="Истекает в ближайшие 7 дней!" withArrow>
                          <Badge color="orange" variant="light" leftSection={<IconAlertTriangle size={12} />}>
                            {formatDate(token.expires_at)}
                          </Badge>
                        </Tooltip>
                      ) : (
                        <Text size="xs" c={tokens.colors.neutral[6]}>
                          {formatDate(token.expires_at)}
                        </Text>
                      )
                    ) : (
                      <Badge color="blue" variant="light" size="sm">
                        Бессрочно
                      </Badge>
                    )}
                  </Table.Td>

                  {/* Last used */}
                  <Table.Td>
                    {token.last_used_at ? (
                      <Tooltip
                        label={`IP: ${token.last_used_ip || 'неизвестно'}`}
                        withArrow
                      >
                        <Group gap={4}>
                          <IconClock size={14} color={tokens.colors.neutral[5]} />
                          <Text size="xs" c={tokens.colors.neutral[6]}>
                            {formatDate(token.last_used_at)}
                          </Text>
                        </Group>
                      </Tooltip>
                    ) : (
                      <Text size="xs" c={tokens.colors.neutral[5]} fs="italic">
                        Не использовался
                      </Text>
                    )}
                  </Table.Td>

                  {/* Actions */}
                  <Table.Td>
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          loading={actionLoading === token.id}
                        >
                          <IconDots size={18} />
                        </ActionIcon>
                      </Menu.Target>

                      <Menu.Dropdown>
                        {token.is_active && (
                          <Menu.Item
                            leftSection={<IconBan size={16} />}
                            color="orange"
                            onClick={() => handleRevoke(token.id, token.name)}
                          >
                            Деактивировать
                          </Menu.Item>
                        )}
                        <Menu.Item
                          leftSection={<IconTrash size={16} />}
                          color="red"
                          onClick={() => handleDeleteClick(token)}
                        >
                          Удалить навсегда
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Delete confirmation modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        title="Подтверждение удаления"
        centered
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            <Text size="sm">
              Вы действительно хотите <strong>безвозвратно удалить</strong> токен{' '}
              <strong>"{tokenToDelete?.name}"</strong>?
            </Text>
            <Text size="sm" mt="xs">
              Это действие нельзя отменить!
            </Text>
          </Alert>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setDeleteModalOpened(false)}
            >
              Отмена
            </Button>
            <Button
              variant="filled"
              color="red"
              onClick={handleDeleteConfirm}
              loading={actionLoading === tokenToDelete?.id}
            >
              Удалить навсегда
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default APITokensList;
